import { IHttpResponse } from 'angular';
import {
  Collection,
  AddRemoveCollectionItemsData,
  CollectionError,
  DeleteCollectionData,
  CollectionContentItem,
  CollectionItem
} from '../../app/spaces/Collections/CollectionModels';
import {
  GetCollectionsResponse,
  AddRemoveItemsResponse,
  DeleteResponse,
  GetCollectionItemsResponse,
  GetCollectionResponse
} from '../../app/spaces/Collections/Collections';
import { ICollectionInfo } from '../../app/spaces/Collections/ICollectionInfo';
import { IGetCollectionResult } from '../../app/spaces/Collections/IGetCollectionResult';
import { ListResult } from '../fetchers/ListResult';
import { MapperHelper } from './MapperHelper';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { IContentInfo } from '../models/IContentInfo';
import { Set } from '../models/Set';
import { mapContentFromCrass } from '../resources/Search';
import { IFavorite } from '../ServerApi/types/IFavorite';

export module CollectionMapper {
  function mapCollectionToCollectionInfo(collection: Collection): ICollectionInfo {
    return {
      luid: collection.luid,
      id: collection.id + '',
      name: collection.name,
      description: collection.description,
      contentType: 'collection',
      totalItemCount: collection.totalItemCount,
      permissionedItemCount: collection.permissionedItemCount,
      userId: collection.userId,
      visibility: collection.visibility,
      createdAt: MapperHelper.toDate(collection.createdAt),
      updatedAt: MapperHelper.toDate(collection.updatedAt),
      favorite: collection.isFavorite
    };
  }

  export function mapGetCollectionResult(response: IHttpResponse<GetCollectionResponse>): IGetCollectionResult {
    return {
      collection: mapCollectionToCollectionInfo(response.data.collection),
      errors: response.data.errors ? response.data.errors.map(error => ({ code: error.errorCode })) : [],
    };
  }

  export function mapsSingle(response: IHttpResponse<Collection>): ICollectionInfo {
    return mapCollectionToCollectionInfo(response.data);
  }

  export function mapList(response: IHttpResponse<GetCollectionsResponse>, start: number): ListResult<ICollectionInfo> {
    return {
      items: response.data.items.map(mapCollectionToCollectionInfo),
      total: response.data.totalCount,
      index: start,
      errors: response.data.errors.map(error => ({
        code: error.errorCode
      })),
    };
  }

  export function mapFromCRASS(content: any): ICollectionInfo {
    return {
      contentType: 'collection',
      luid: content.luid,
      name: content.name,
      id: content.id + '',
      userId: content.ownerId,
      totalItemCount: content.itemNames.length,
      permissionedItemCount: -1, // We don't know when fetching collections from crass
      visibility: content.visibility,
      description: content.description,
      createdAt: MapperHelper.toDate(content.createdTime) || MapperHelper.toDateFromTimeOffsetObject(content.createdTime),
      updatedAt: MapperHelper.toDate(content.modifiedTime) || MapperHelper.toDateFromTimeOffsetObject(content.modifiedTime),
      favorite: false,
      owner: {
        id: content.ownerId + '',
        displayName: content.ownerName || '',
        username: content.ownerAlias || '',
        domainName: content.ownerDomain || '',
        isHiddenInternalUser: false
      }
    };
  }

  export function mapAddRemoveItemsResponse(response: IHttpResponse<AddRemoveItemsResponse>, luid?: string): AddRemoveCollectionItemsData {
    return {
      numItemsSuccessfullyUpdated: response.data.response,
      errors: response.data.errors,
      collectionLuid: luid,
    };
  }

  export function mapItemsList(response: IHttpResponse<GetCollectionItemsResponse>, start: number, size: number, favorites: _.Dictionary<IFavorite>): ListResult<CollectionContentItem> {
    const contentItems = response.data.items.filter((item) => item.content);
    return {
      items: contentItems
        .map(item => ({
          ...mapContentFromCrass(item),
          position: item.position,
          collectionLuid: item.collectionLuid,
          favorite: isFavorite(item, favorites)
        })),
      total: size === 0 ? response.data.totalCount : contentItems.length,
      index: start,
      errors: response.data.errors ? response.data.errors.map(error => ({ code: error.errorCode })) : [],
    };
  }

  export function isFavorite(item: CollectionItem, favorites: _.Dictionary<IFavorite>) {
    if (favorites[item.contentLuid]) {
      return true;
    }

    // Data source collection items return datasourceLuid as the contentLuid
    // favorites return the other data source luid, so check favorites map
    // by the regular luid
    if(item.content.contentType==='unifieddatasource') {
      return !!favorites[item.content.luid];
    }

    return false;
  }

  function mapCollectionErrorToIError(collectionError: CollectionError, collection: IContentInfo): IError {
    return {
      code: collectionError.errorCode,
      id: collection.id
    };
  }

  export function mapDeleteCollectionsDataToIResult(data: DeleteCollectionData, collections: Set<IContentInfo>): IResult {
    const mappedErrors = data.errors.map(err => mapCollectionErrorToIError(err, collections.filter((col: any) => err.luid === col.luid).getByIndex(0)));
    return {
      errors: mappedErrors
    };
  }

  export function mapDeleteCollectionsResponse(response: IHttpResponse<DeleteResponse>): DeleteCollectionData {
    return {
      successfulLuids: response.data.response,
      errors: response.data.errors
    };
  }
}
